import React, { useMemo } from "react";
import { Col, ColProps, Row, RowProps } from "antd";
import { useWindowSizeType } from "@helpers/Hooks";
import {
	DEFAULT_LAYOUT_COLUMN,
	GRID_ROW_STYLE,
	MAX_LAYOUT_COLUMNS,
} from "./data";

type GridColProps = ColProps & {
	/** if value is not provided, it would be full */
	layoutColumns?: {
		mobile?: number;
		tablet?: number;
		laptop?: number;
		desktop?: number;
	};
};

export function GridCol({
	layoutColumns,
	children,
	...restProps
}: GridColProps) {
	const windowSizeType = useWindowSizeType();
	const colSpan = useMemo(() => {
		let type =
			windowSizeType === "tablet_small"
				? "tablet"
                : windowSizeType;
        let span: number = MAX_LAYOUT_COLUMNS[type];
        if(layoutColumns && typeof layoutColumns[type] === "number"){
            span = layoutColumns[type] as number;
        }
		return (
			(DEFAULT_LAYOUT_COLUMN /
				MAX_LAYOUT_COLUMNS[type]) *
			span
		);
	}, [windowSizeType]);

	return (
		<Col span={colSpan} {...restProps}>
			{children}
		</Col>
	);
}

type GridRowProps = RowProps & {
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	verticalGutter?: number;
};

export function GridRow({
	children,
	className = "",
	style,
	verticalGutter = 20,
	...restProps
}: GridRowProps) {
	const windowSizeType = useWindowSizeType();

	return (
		<Row
			align="middle"
			justify="start"
			gutter={[
				GRID_ROW_STYLE[windowSizeType].gutter,
				verticalGutter,
			]}
			style={{
				...style,
				marginLeft:
					GRID_ROW_STYLE[windowSizeType].margin ||
					"auto",
				marginRight:
					GRID_ROW_STYLE[windowSizeType].margin ||
					"auto",
				width: style?.width || GRID_ROW_STYLE[windowSizeType].width,
			}}
			className={className}
			{...restProps}
		>
			{children}
		</Row>
	);
}

type SpaceColProps = {
	space?: number;
	/** remove the column if screen width is less then laptop size */
	hideLtLaptop?: boolean;
};
/**
 * The component which is one empty column on `desktop` and `tablet`.
 */
export function SpaceCol({
	space = 1,
	hideLtLaptop = true,
}: SpaceColProps) {
	const windowSizeType = useWindowSizeType();
	if (
		hideLtLaptop &&
		windowSizeType !== "desktop" &&
		windowSizeType !== "laptop"
	) {
		return <></>;
	}
	return (
		<GridCol
			layoutColumns={{
				mobile: space,
				tablet: space,
				laptop: space,
				desktop: space,
			}}
		/>
	);
}

/**
 * SkeletonGridLayout
 * show Grid Layout as background.
 */
export function SkeletonGridLayout() {
	const windowSizeType = useWindowSizeType();
	return (
		<div
			style={{
				position: "absolute",
				width: "100vw",
				height: "100vh",
				zIndex: 999,
			}}
		>
			<GridRow>
				{Array(MAX_LAYOUT_COLUMNS[windowSizeType])
					.fill(0)
					.map((_, index) => (
						<GridCol
							key={`gridsystem-${windowSizeType}-${index}`}
							layoutColumns={{
								mobile: 1,
								tablet: 1,
								laptop: 1,
								desktop: 1,
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent:
										"center",
									alignItems: "center",
									height: "100vh",
									background: "#bbf8f8",
									opacity: 0.5,
								}}
							>
								<h1>{index + 1}</h1>
							</div>
						</GridCol>
					))}
			</GridRow>
		</div>
	);
}
